<template>
  <div class="match-start-container">
    <h4 class="text-center mt-2 mb-0"><font-awesome-icon :icon="['fas', 'play-circle']" /> 試合開始スキャン</h4>
    <div class="scanner-section">
      <h5 class="text-center mb-3">QRコードをスキャンしてください</h5>
      <div id="qr-reader" class="qr-reader"></div>

      <div v-if="statusMessage" class="alert mt-3 mx-3" :class="statusMessageClass">{{ statusMessage }}</div>
      <div v-if="scanError" class="alert alert-danger mt-3 mx-3">{{ scanError }}</div>
      <div v-if="!cameraAvailable && cameraChecked" class="alert alert-warning mt-3 mx-3">
        <font-awesome-icon :icon="['fas', 'camera']" /> カメラを起動できませんでした。<br>
        HTTPS環境またはlocalhostでアクセスしてください。
      </div>
    </div>

    <div class="modal fade" id="successModal" tabindex="-1" aria-labelledby="successModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="successModalLabel">{{ matchKey }} を試合中に更新しました</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p class="text-center mb-2">コート番号を指定してください</p>
            <div class="row g-2 mb-2">
              <div class="col-6 col-md-4 col-lg-3" v-for="i in 10" :key="i">
                <div class="card court-card" :class="{ 'border-primary': selectedCourt === i, 'bg-primary': selectedCourt === i, 'text-white': selectedCourt === i }" 
                     @click="selectCourt(i)" style="cursor: pointer;">
                  <div class="card-body text-center p-2">
                    <h6 class="card-title mb-0 court-number">{{ i }}</h6>
                  </div>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-12">
                <label for="actualCourtInput" class="form-label">実コート番号（自由入力）</label>
                <input type="text" class="form-control" id="actualCourtInput" v-model="actualCourt" placeholder="実コート番号を入力">
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-primary btn-lg" @click="saveCourt" style="min-width: 120px;">保存</button>
          </div>
        </div>
      </div>
    </div>

    <div class="modal fade" id="editMatchModal" tabindex="-1" aria-labelledby="editMatchModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="editMatchModalLabel">試合結果編集</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <EditMatchScoreFormComponent :match="matchData" :matchKey="matchKey" v-if="matchData" />
          </div>
        </div>
      </div>
    </div>

    <ScanHistoryComponent
      :entries="currentEntries"
      :availableDates="availableDates"
      :selectedDate="selectedDate"
      @update:selectedDate="selectedDate = $event"
    />

    <FooterComponent />
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { Modal } from 'bootstrap'
import { Html5Qrcode } from 'html5-qrcode'
import { ref as fbRef, getDatabase, get, child, update } from 'firebase/database'
import EditMatchScoreFormComponent from '@/components/EditMatchScoreFormComponent.vue'
import FooterComponent from '@/components/FooterComponent.vue'
import ScanHistoryComponent from '@/components/ScanHistoryComponent.vue'
import { useScanHistory } from '@/composables/useScanHistory'

const db = getDatabase()
const matchData = ref(null)
const matchKey = ref('')
const scanError = ref('')
const statusMessage = ref('')
const statusMessageClass = ref('')
const cameraAvailable = ref(false)
const { selectedDate, availableDates, currentEntries, addEntry } = useScanHistory('scanHistory_start')
const cameraChecked = ref(false)
const selectedCourt = ref(null)
const actualCourt = ref('')
let html5Qrcode = null
let editModal = null
let successModal = null
let processing = false
const successSound = new Audio('/sound/success1.mp3')
const confirmationSound = new Audio('/sound/confirmation1.mp3')
const errorSound = new Audio('/sound/error1.mp3')
const saveCourtSound = new Audio('/sound/save-court.mp3')

const extractMatchKey = (text) => {
  const match = text.match(/\/match\/([A-Za-z0-9]+-[A-Za-z0-9]+)/)
  return match ? match[1] : null
}

const loadMatch = async (rawText) => {
  if (!rawText || processing) return
  processing = true
  const key = extractMatchKey(rawText)
  if (!key) {
    scanError.value = 'QRコードの形式が正しくありません'
    addEntry('(Path error)', 'error')
    errorSound.play().catch(() => {})
    setTimeout(() => { processing = false }, 800)
    return
  }
  scanError.value = ''
  statusMessage.value = ''
  matchKey.value = key

  try {
    const snapshot = await get(child(fbRef(db), `/2026-summer/matches/${key}`))
    if (snapshot.exists()) {
      const data = snapshot.val()
      matchData.value = data

      if (data.match_status === 'called' || data.match_status === 'called_finished') {
        const updates = {}
        updates[`/2026-summer/matches/${key}/match_status`] = 'playing'
        await update(fbRef(db), updates)
        matchData.value.match_status = 'playing'
        addEntry(key, 'success')
        successSound.play().catch(() => {})
        if (navigator.vibrate) navigator.vibrate(300)
        
        // モーダルを表示
        await stopScanner()
        processing = false  // processingフラグをリセット
        nextTick(() => {
          if (!successModal) {
            successModal = new Modal(document.getElementById('successModal'))
            document.getElementById('successModal').addEventListener('hidden.bs.modal', onSuccessModalClosed)
          }
          selectedCourt.value = null
          actualCourt.value = data.actual_court || ''  // 既存のactual_courtを表示
          successModal.show()
        })
      } else {
        addEntry(key, 'confirmation')
        confirmationSound.play().catch(() => {})
        await stopScanner()
        nextTick(() => {
          if (!editModal) {
            editModal = new Modal(document.getElementById('editMatchModal'))
            document.getElementById('editMatchModal').addEventListener('hidden.bs.modal', onModalClosed)
          }
          editModal.show()
        })
        setTimeout(() => { processing = false }, 800)
      }
    } else {
      scanError.value = `試合が見つかりません: ${key}`
      addEntry(key, 'error')
      errorSound.play().catch(() => {})
      setTimeout(() => { processing = false }, 800)
    }
  } catch (e) {
    scanError.value = `データ取得エラー: ${e.message}`
    addEntry(key, 'error')
    errorSound.play().catch(() => {})
    setTimeout(() => { processing = false }, 800)
  }
}

const onScanSuccess = async (decodedText) => {
  await loadMatch(decodedText)
}

const startScanner = async () => {
  matchData.value = null
  try {
    // 毎回新しいインスタンスを作成
    if (html5Qrcode) {
      try {
        await html5Qrcode.stop()
      } catch (e) {
        // already stopped
      }
      html5Qrcode = null
    }
    
    html5Qrcode = new Html5Qrcode('qr-reader')
    await html5Qrcode.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: { width: 175, height: 175 } },
      onScanSuccess
    )
    cameraAvailable.value = true
  } catch (e) {
    console.error('スキャナー起動エラー:', e)
    cameraAvailable.value = false
  }
  cameraChecked.value = true
}

const stopScanner = async () => {
  if (html5Qrcode) {
    try {
      await html5Qrcode.stop()
    } catch (e) {
      // already stopped
    }
  }
}

const onModalClosed = () => {
  matchData.value = null
  matchKey.value = ''
  scanError.value = ''
  statusMessage.value = ''
  cameraChecked.value = false
  setTimeout(() => startScanner(), 50)  // 遅延を短縮
}

const onSuccessModalClosed = () => {
  selectedCourt.value = null
  actualCourt.value = ''
  matchData.value = null
  matchKey.value = ''
  scanError.value = ''
  statusMessage.value = ''
  cameraChecked.value = false
  setTimeout(() => startScanner(), 50)  // 遅延を短縮
}

const selectCourt = (courtNumber) => {
  selectedCourt.value = courtNumber
  actualCourt.value = courtNumber.toString()
}

const saveCourt = async () => {
  if (!matchKey.value) return
  
  console.log('保存するデータ:', {
    matchKey: matchKey.value,
    actual_court: actualCourt.value
  })
  
  try {
    const updates = {}
    updates[`/2026-summer/matches/${matchKey.value}/actual_court`] = actualCourt.value
    console.log('更新オブジェクト:', updates)
    
    await update(fbRef(db), updates)
    console.log('保存成功')
    
    saveCourtSound.play().catch(() => {})
    if (navigator.vibrate) navigator.vibrate(200)
    
    successModal.hide()
  } catch (e) {
    console.error('コート番号の保存に失敗しました:', e)
  }
}

onMounted(() => {
  nextTick(() => {
    setTimeout(() => startScanner(), 300)
  })
})

onBeforeUnmount(async () => {
  await stopScanner()
})
</script>

<style scoped>
.match-start-container {
  min-height: 100vh;
  padding-bottom: 80px;
}

.scanner-section {
  padding-top: 20px;
}

.qr-reader {
  max-width: 350px;
  margin: 0 auto;
}

.court-card {
  transition: all 0.2s ease;
  border: 2px solid #dee2e6;
}

.court-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.court-number {
  font-size: 2rem;
  font-weight: bold;
}
</style>
